/**
 * SalvationConstants
 *
 * Mirror of `backend/Libraries/CCMSLib.Models/Salvation/SalvationConstants.cs`.
 * Seeded lookup IDs (274a_SalvationManagement_seed_pg.sql). The lookup TABLES are the source of truth for
 * names/colours/icons (see `SalvationLookupsView`); these constants only name the fixed IDs the engine branches on.
 * IDs start at 1, never 0. Keep additive.
 *
 * Each C# nested class is a `SALVATION_*` const object. `SalvationConstants` at the bottom groups them under the
 * C# names (`SalvationConstants.ConvertStatus.Met`).
 */
/** The service name API keys must list in AllowedServices. */
export const SALVATION_SERVICE_NAME = 'salvation-management';
/** AppRegistry.AppName (pg/system/021). AppID is assigned by the database; look it up by name. */
export const SALVATION_APP_NAME = 'SalvationManagement';
/** Source / audit tag other services record for writes the Salvation Team makes. */
export const SALVATION_SOURCE = 'salvation-team';
export const SALVATION_CONVERT_STATUS = {
    NotContacted: 1,
    Tried: 2,
    Reached: 3,
    Met: 4,
    HandedOver: 5,
    Connected: 6,
    Declined: 7,
    Closed: 8,
};
/** Still in follow-up (the Lead / Backup rules apply). */
export const salvationIsFollowUp = (statusId) => statusId >= SALVATION_CONVERT_STATUS.NotContacted && statusId <= SALVATION_CONVERT_STATUS.Met;
/** Nobody has reached them yet: the deadline and tries rules apply. */
export const salvationIsUnreached = (statusId) => statusId === SALVATION_CONVERT_STATUS.NotContacted || statusId === SALVATION_CONVERT_STATUS.Tried;
/** On the growth journey (milestones apply). */
export const salvationIsGrowing = (statusId) => statusId === SALVATION_CONVERT_STATUS.HandedOver || statusId === SALVATION_CONVERT_STATUS.Connected;
/** SalvationConvertStatus.StageID. */
export const SALVATION_STAGE = {
    FollowUp: 1,
    Growing: 2,
    Connected: 3,
    Closed: 4,
};
export const SALVATION_FOLLOW_UP_TYPE = {
    Attempt: 1,
    Reached: 2,
    Met: 3,
    HandedOver: 4,
    Declined: 5,
    Note: 6,
    Reopened: 7,
    BackupNowResponsible: 8,
    Assigned: 9,
    AssignmentCorrected: 10,
    Connected: 11,
    Closed: 12,
    Milestone: 13,
    Escalated: 14,
    Added: 15,
};
export const SALVATION_RESPONSIBILITY_REASON = {
    Lead: 1,
    DeadlinePassed: 2,
    TriesUsedUp: 3,
    LeadNotActive: 4,
};
export const SALVATION_INTAKE_CHANNEL = {
    TypedIn: 1,
    DecisionCard: 2,
    PlanningCentreForm: 3,
    Anatomy: 4,
    LegacyApp: 5,
    /** 274c: people on a Planning Centre People list. */
    PlanningCentreList: 6,
    /** 274c: rows read in the browser from a CSV or Excel file. */
    Spreadsheet: 7,
};
export const SALVATION_INTAKE_STATUS = {
    Waiting: 1,
    Accepted: 2,
    Joined: 3,
    Rejected: 4,
};
export const SALVATION_MILESTONE_KIND = {
    Manual: 1,
    /** A call centre journey step (KindRef = JourneyStep.StepID). */
    Course: 2,
    ServingTeam: 3,
    Baptism: 4,
    ConnectGroup: 5,
};
export const SALVATION_MILESTONE_STATUS = {
    Ready: 1,
    Booked: 2,
    Done: 3,
    Cancelled: 4,
};
/** SalvationFollowUp.Source and SalvationConvertMilestone.Source. */
export const SALVATION_WRITE_SOURCE = {
    Portal: 'portal',
    Console: 'console',
    System: 'system',
    Intake: 'intake',
    Import: 'import',
    CallCentre: 'call-centre',
};
/** AspNetRights names (pg/identity/012). */
export const SALVATION_RIGHTS = {
    Read: 'Salvation.Read',
    ConvertsManage: 'Salvation.Converts.Manage',
    Assign: 'Salvation.Assign',
    AssignmentsCorrect: 'Salvation.Assignments.Correct',
    TeamManage: 'Salvation.Team.Manage',
    IntakeReview: 'Salvation.Intake.Review',
    MilestonesManage: 'Salvation.Milestones.Manage',
    Export: 'Salvation.Export',
    SettingsManage: 'Salvation.Settings.Manage',
    FollowUpLog: 'Salvation.FollowUp.Log',
};
/** AspNetRoles names (pg/identity/012). */
export const SALVATION_ROLES = {
    Admin: 'Admin',
    SalvationAdmin: 'SalvationAdmin',
    SalvationLeader: 'SalvationLeader',
    SalvationVolunteer: 'SalvationVolunteer',
    /** Every role that may open the console module (rights and campus narrow it further). */
    ConsoleRoles: ['Admin', 'SalvationAdmin', 'SalvationLeader'],
    /** Roles that may change settings and every campus. */
    FullAccessRoles: ['Admin', 'SalvationAdmin'],
};
/** Communication worker TemplateIDs (block 66000-66029). */
export const SALVATION_TEMPLATES = {
    BlockStart: 66000,
    BlockEnd: 66029,
};
/** Error codes carried in ResponseData.errors[0] (the portal and console branch on these). */
export const SALVATION_ERROR_CODES = {
    /** Signed in, but not on a Salvation Team yet: the portal shows "ask your campus leader". */
    NoAccess: 'no_access',
    /** The person is not one of this volunteer's people. */
    NotAssigned: 'not_assigned',
    /** The campus is not one this leader leads. */
    NotYourCampus: 'not_your_campus',
    ConvertNotFound: 'convert_not_found',
    TeamNotFound: 'team_not_found',
    /** 409: errors[1] = "convertId:N" (the possible duplicate). */
    DuplicateConvert: 'duplicate_convert',
    AssignmentPermanent: 'assignment_permanent',
    SameLeadAndBackup: 'same_lead_and_backup',
    NotEnoughTeam: 'not_enough_team',
    HandOverNeedsMet: 'handover_needs_met',
    NotInFollowUp: 'not_in_follow_up',
    InvalidPhone: 'invalid_phone',
    ContactRequired: 'contact_required',
    Stale: 'stale',
    MemberServiceUnavailable: 'member_service_unavailable',
};
export const SALVATION_LIMITS = {
    MaxPageSize: 200,
    MaxNoteLength: 500,
    MaxReasonLength: 500,
    MinTeamForAllocation: 2,
    /** Default and largest number of people pulled from one source at a time. */
    DefaultPullMax: 2000,
    MaxPullRows: 5000,
    MaxIntakeAction: 500,
};
/** The C# constants under their C# names. */
export const SalvationConstants = {
    ServiceName: SALVATION_SERVICE_NAME,
    AppName: SALVATION_APP_NAME,
    Source: SALVATION_SOURCE,
    ConvertStatus: {
        ...SALVATION_CONVERT_STATUS,
        isFollowUp: salvationIsFollowUp,
        isUnreached: salvationIsUnreached,
        isGrowing: salvationIsGrowing,
    },
    Stage: SALVATION_STAGE,
    FollowUpType: SALVATION_FOLLOW_UP_TYPE,
    ResponsibilityReason: SALVATION_RESPONSIBILITY_REASON,
    IntakeChannel: SALVATION_INTAKE_CHANNEL,
    IntakeStatus: SALVATION_INTAKE_STATUS,
    MilestoneKind: SALVATION_MILESTONE_KIND,
    MilestoneStatus: SALVATION_MILESTONE_STATUS,
    WriteSource: SALVATION_WRITE_SOURCE,
    Rights: SALVATION_RIGHTS,
    Roles: SALVATION_ROLES,
    Templates: SALVATION_TEMPLATES,
    ErrorCodes: SALVATION_ERROR_CODES,
    Limits: SALVATION_LIMITS,
};
//# sourceMappingURL=SalvationConstants.js.map